import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useParams, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container, Box, Typography, Button, Grid, Chip, Select, MenuItem,
  FormControl, TextField, RadioGroup, FormControlLabel, Radio, Collapse,
  Accordion, AccordionSummary, AccordionDetails, Divider,
  InputAdornment, useMediaQuery, useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import BeachAccessOutlinedIcon from '@mui/icons-material/BeachAccessOutlined';
import BalanceOutlinedIcon from '@mui/icons-material/BalanceOutlined';
import SEOHead from '../components/SEO/SEOHead';
import BreadcrumbNav from '../components/SEO/BreadcrumbNav';
import ReferralCTA from '../components/shared/ReferralCTA';
import FAQSection from '../components/SEO/FAQSection';
import NewsletterSection from '../components/Layout/NewsletterSection';
import WizardStep from '../components/CalculadoraRRSP/WizardStep';
import StickyResumen from '../components/CalculadoraRRSP/StickyResumen';
import PriorityCards from '../components/CalculadoraRRSP/PriorityCards';
import ComparisonTable from '../components/CalculadoraRRSP/ComparisonTable';
import ProjectionChart from '../components/CalculadoraRRSP/ProjectionChart';
import NextSteps from '../components/CalculadoraRRSP/NextSteps';
import ShareBar from '../components/CalculadoraRRSP/ShareBar';
import { PROVINCES, getPriority, getMarginalRate, calcTFSARoom, calcRRSPRoom, TAX_DATA } from '../data/taxBrackets';
import { useLocalizedPath } from '../hooks/useLocalizedPath';

const YEARS_OPTIONS = [2026, 2025, 2024, 2023, 2022, 2021, 2020];
const TOTAL_STEPS = 4;

const OBJECTIVE_ICONS = {
  home: HomeOutlinedIcon,
  taxes: SavingsOutlinedIcon,
  flexibility: LockOpenOutlinedIcon,
  retirement: BeachAccessOutlinedIcon,
  balanced: BalanceOutlinedIcon,
};

const CalculadoraRRSPPage = () => {
  const { lng } = useParams();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const getPath = useLocalizedPath();
  const [searchParams] = useSearchParams();

  // ── State ──
  const [step, setStep] = useState(1);
  const [province, setProvince] = useState('QC');
  const [yearsInCanada, setYearsInCanada] = useState('');
  const [objective, setObjective] = useState('');
  const [income, setIncome] = useState('');
  const [employerMatch, setEmployerMatch] = useState('');
  const [hasEmergencyFund, setHasEmergencyFund] = useState('');
  const [contribution, setContribution] = useState('');
  const [frequency, setFrequency] = useState('monthly');
  const [previousIncome, setPreviousIncome] = useState('');
  const [showOptional, setShowOptional] = useState(false);
  const [showResults, setShowResults] = useState(false);

  // ── Load from URL params ──
  useEffect(() => {
    const p = searchParams.get('prov');
    if (p) setProvince(p.toUpperCase());
    const y = searchParams.get('year');
    if (y) setYearsInCanada(y);
    const o = searchParams.get('obj');
    if (o) setObjective(o);
    const inc = searchParams.get('income');
    if (inc) setIncome(inc);
    const m = searchParams.get('match');
    if (m) setEmployerMatch(m);
    const c = searchParams.get('contribution');
    if (c) setContribution(c);
    // If all params present, jump to results
    if (p && o && inc) {
      setShowResults(true);
      setStep(TOTAL_STEPS);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Track calculator start ──
  useEffect(() => {
    if (window.plausible) window.plausible('Calculator Started', { props: { tool: 'rrsp_vs_tfsa' } });
  }, []);

  // ── Minimize Maya during calculator wizard ──
  useEffect(() => {
    document.body.classList.add('maya-minimized');
    return () => document.body.classList.remove('maya-minimized');
  }, []);

  // ── Derived calculations ──
  const yearNum = yearsInCanada === 'before2020' ? 2019 : yearsInCanada === 'unknown' ? null : Number(yearsInCanada);
  const incomeNum = Number(income) || 0;

  const result = useMemo(() => {
    if (!showResults) return null;
    return getPriority({
      income: incomeNum,
      province,
      objective: objective || 'balanced',
      employerMatch,
      yearsInCanada: yearNum,
      hasEmergencyFund,
      previousIncome,
    }, t);
  }, [showResults, incomeNum, province, objective, employerMatch, yearNum, hasEmergencyFund, previousIncome, t]);

  const handleNext = useCallback(() => {
    if (step < TOTAL_STEPS) {
      setStep(s => s + 1);
    } else {
      setShowResults(true);
      // Track
      if (window.plausible) {
        window.plausible('Calculator Complete', { props: { calculator: 'rrsp_vs_tfsa', province } });
      }
    }
    // Track step
    if (window.plausible) window.plausible('Calculator Step', { props: { step } });
  }, [step]);

  const handleBack = useCallback(() => {
    if (step > 1) setStep(s => s - 1);
  }, [step]);

  const handleEditFromResults = () => {
    setShowResults(false);
    setStep(1);
  };

  const handleRestart = () => {
    setShowResults(false);
    setStep(1);
    setProvince('QC');
    setYearsInCanada('');
    setObjective('');
    setIncome('');
    setEmployerMatch('');
    setHasEmergencyFund('');
    setPreviousIncome('');
    setContribution('');
    setFrequency('monthly');
    setShowOptional(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── SEO ──
  const breadcrumbs = [
    { label: t('calc.breadcrumbTools'), to: getPath('tools') },
    { label: t('calc.breadcrumbCurrent') },
  ];

  const faqs = Array.from({ length: 8 }, (_, i) => ({
    question: t(`calc.faq${i + 1}Q`),
    answer: t(`calc.faq${i + 1}A`),
  }));

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(f => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  // ── Render ──
  return (
    <>
      <SEOHead
        title={t('calc.seoTitle')}
        description={t('calc.seoDescription')}
        canonical={`/${lng}/herramientas/calculadora-rrsp-vs-tfsa`}
        schema={faqSchema}
      />

      <Container maxWidth="lg" sx={{ pt: { xs: 3, md: 5 }, pb: 8 }}>
        {/* Breadcrumb */}
        <BreadcrumbNav items={breadcrumbs} />

        {/* Hero */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 5 }, mt: 2 }}>
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: '1.6rem', md: '2.2rem' },
              fontWeight: 800, color: '#1A1A1A', mb: 1.5, lineHeight: 1.2,
            }}
          >
            {t('calc.heroTitle')}
          </Typography>
          <Typography sx={{ fontSize: { xs: 15, md: 18 }, color: '#5F6B7A', mb: 2.5, maxWidth: 600, mx: 'auto' }}>
            {t('calc.heroSubtitle')}
          </Typography>

          {/* Trust chips */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1 }}>
            {['trustUpdated', 'trustSources', 'trustEstimate', 'trustPrivacy'].map((key) => (
              <Chip
                key={key}
                label={t(`calc.${key}`)}
                size="small"
                sx={{ bgcolor: '#F7F9FC', color: '#5F6B7A', fontSize: 12, fontWeight: 500 }}
              />
            ))}
          </Box>
        </Box>

        {/* ── WIZARD + STICKY SUMMARY ── */}
        {!showResults && (
          <Grid container spacing={4}>
            {/* Wizard */}
            <Grid item xs={12} md={7}>
              <Box
                sx={{
                  bgcolor: '#fff', border: '1px solid #EEF0F3', borderRadius: 4,
                  p: { xs: 3, md: 4 }, minHeight: 340,
                }}
              >
                {/* Step 1: Context */}
                {step === 1 && (
                  <WizardStep step={1} totalSteps={TOTAL_STEPS} title={t('calc.step1.title')}>
                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}>{t('calc.step1.province')}</Typography>
                      <Select
                        value={province}
                        onChange={(e) => setProvince(e.target.value)}
                        size="small"
                        sx={{ borderRadius: 2 }}
                      >
                        {PROVINCES.map((p) => (
                          <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                        ))}
                      </Select>
                      <Typography sx={{ fontSize: 12, color: '#5F6B7A', mt: 0.5 }}>{t('calc.step1.provinceHelper')}</Typography>
                    </FormControl>

                    <FormControl fullWidth>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}>{t('calc.step1.yearsInCanada')}</Typography>
                      <Select
                        value={yearsInCanada}
                        onChange={(e) => setYearsInCanada(e.target.value)}
                        size="small"
                        displayEmpty
                        sx={{ borderRadius: 2 }}
                      >
                        <MenuItem value="" disabled>—</MenuItem>
                        {YEARS_OPTIONS.map((y) => (
                          <MenuItem key={y} value={String(y)}>{y}</MenuItem>
                        ))}
                        <MenuItem value="before2020">{t('calc.step1.beforeOption')}</MenuItem>
                        <MenuItem value="unknown">{t('calc.step1.dontKnow')}</MenuItem>
                      </Select>
                      <Typography sx={{ fontSize: 12, color: '#5F6B7A', mt: 0.5 }}>{t('calc.step1.yearsHelper')}</Typography>
                    </FormControl>

                    {/* Warning for new residents */}
                    <Collapse in={yearsInCanada === '2026' || yearsInCanada === 'unknown'}>
                      <Box sx={{ mt: 2, p: 2, bgcolor: '#FFF8E1', borderRadius: 2, display: 'flex', gap: 1 }}>
                        <WarningAmberIcon sx={{ color: '#F9A825', fontSize: 20, mt: 0.2 }} />
                        <Typography sx={{ fontSize: 13, color: '#5F4B00', lineHeight: 1.6 }}>
                          {t('calc.step1.newResidentWarning')}
                        </Typography>
                      </Box>
                    </Collapse>
                  </WizardStep>
                )}

                {/* Step 2: Objective */}
                {step === 2 && (
                  <WizardStep step={2} totalSteps={TOTAL_STEPS} title={t('calc.step2.title')}>
                    <Grid container spacing={2}>
                      {['home', 'taxes', 'flexibility', 'retirement', 'balanced'].map((obj) => (
                        <Grid item xs={6} key={obj}>
                          <Box
                            onClick={() => setObjective(obj)}
                            sx={{
                              border: `2px solid ${objective === obj ? '#1E3A5F' : '#EEF0F3'}`,
                              borderRadius: 3, p: 2.5, textAlign: 'center', cursor: 'pointer',
                              bgcolor: objective === obj ? '#1E3A5F08' : '#fff',
                              transition: 'all 0.15s',
                              '&:hover': { borderColor: '#1E3A5F80' },
                            }}
                          >
                            {React.createElement(OBJECTIVE_ICONS[obj], {
                              sx: { fontSize: 28, mb: 0.5, color: objective === obj ? '#1E3A5F' : '#5F6B7A' },
                            })}
                            <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1A1A1A' }}>
                              {t(`calc.step2.${obj}`)}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                    <Typography sx={{ fontSize: 12, color: '#5F6B7A', mt: 2, textAlign: 'center' }}>
                      {t('calc.step2.helper')}
                    </Typography>
                  </WizardStep>
                )}

                {/* Step 3: Income */}
                {step === 3 && (
                  <WizardStep step={3} totalSteps={TOTAL_STEPS} title={t('calc.step3.title')}>
                    <Box sx={{ mb: 3 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}>{t('calc.step3.income')}</Typography>
                      <TextField
                        value={income ? Number(income).toLocaleString('en-CA') : ''}
                        onChange={(e) => setIncome(e.target.value.replace(/[^0-9]/g, ''))}
                        placeholder="55,000"
                        size="small"
                        fullWidth
                        inputMode="numeric"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          endAdornment: <InputAdornment position="end">CAD</InputAdornment>,
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                      <Typography sx={{ fontSize: 12, color: '#5F6B7A', mt: 0.5 }}>{t('calc.step3.incomeHelper')}</Typography>
                      {/* Fix 5: high income confirmation */}
                      <Collapse in={Number(income) > 200000}>
                        <Box sx={{ mt: 1, p: 1.5, bgcolor: '#FFF8E1', borderRadius: 2 }}>
                          <Typography sx={{ fontSize: 12, color: '#5F4B00' }}>
                            {t('calc.step3.highIncomeConfirm', { amount: Number(income).toLocaleString('en-CA') })}
                          </Typography>
                        </Box>
                      </Collapse>
                    </Box>

                    {/* Fix 1: Previous Canadian income question */}
                    <Box sx={{ mb: 3 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}>{t('calc.step3.previousIncome')}</Typography>
                      <RadioGroup row value={previousIncome} onChange={(e) => setPreviousIncome(e.target.value)}>
                        <FormControlLabel value="yes" control={<Radio size="small" />} label={t('calc.step3.previousYes')} />
                        <FormControlLabel value="no" control={<Radio size="small" />} label={t('calc.step3.previousNo')} />
                        <FormControlLabel value="unknown" control={<Radio size="small" />} label={t('calc.step3.previousUnknown')} />
                      </RadioGroup>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}>{t('calc.step3.employerMatch')}</Typography>
                      <RadioGroup row value={employerMatch} onChange={(e) => setEmployerMatch(e.target.value)}>
                        <FormControlLabel value="yes" control={<Radio size="small" />} label={t('calc.step3.matchYes')} />
                        <FormControlLabel value="no" control={<Radio size="small" />} label={t('calc.step3.matchNo')} />
                        <FormControlLabel value="unknown" control={<Radio size="small" />} label={t('calc.step3.matchUnknown')} />
                      </RadioGroup>

                      <Collapse in={employerMatch === 'unknown'}>
                        <Box sx={{ mt: 1, p: 2, bgcolor: '#E3F2FD', borderRadius: 2 }}>
                          <Typography sx={{ fontSize: 13, color: '#0D47A1', lineHeight: 1.6 }}>
                            {t('calc.step3.matchTip')}
                          </Typography>
                        </Box>
                      </Collapse>
                    </Box>

                    {/* Optional section */}
                    <Box>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => setShowOptional(!showOptional)}
                        sx={{ fontSize: 13, color: '#5F6B7A', textTransform: 'none' }}
                      >
                        {t('calc.step3.optional')} {showOptional ? '▲' : '▼'}
                      </Button>
                      <Collapse in={showOptional}>
                        <Box sx={{ mt: 1 }}>
                          <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}>{t('calc.step3.emergencyFund')}</Typography>
                          <RadioGroup row value={hasEmergencyFund} onChange={(e) => setHasEmergencyFund(e.target.value)}>
                            <FormControlLabel value="yes" control={<Radio size="small" />} label={t('calc.step3.yes')} />
                            <FormControlLabel value="no" control={<Radio size="small" />} label={t('calc.step3.no')} />
                          </RadioGroup>
                        </Box>
                      </Collapse>
                    </Box>
                  </WizardStep>
                )}

                {/* Step 4: Contribution */}
                {step === 4 && (
                  <WizardStep step={4} totalSteps={TOTAL_STEPS} title={t('calc.step4.title')}>
                    <Box sx={{ mb: 3 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}>{t('calc.step4.contribution')}</Typography>
                      <TextField
                        type="number"
                        value={contribution}
                        onChange={(e) => setContribution(e.target.value)}
                        placeholder="500"
                        size="small"
                        fullWidth
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Box>

                    <RadioGroup row value={frequency} onChange={(e) => setFrequency(e.target.value)}>
                      <FormControlLabel value="monthly" control={<Radio size="small" />} label={t('calc.step4.monthly')} />
                      <FormControlLabel value="annual" control={<Radio size="small" />} label={t('calc.step4.annual')} />
                    </RadioGroup>

                    <Typography sx={{ fontSize: 12, color: '#5F6B7A', mt: 1 }}>
                      {t('calc.step4.helper')}
                    </Typography>
                  </WizardStep>
                )}

                {/* Navigation buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                  <Button
                    onClick={handleBack}
                    disabled={step === 1}
                    startIcon={<ArrowBackIcon />}
                    sx={{ textTransform: 'none', color: '#5F6B7A' }}
                  >
                    {t('calc.back')}
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={step === 2 && !objective}
                    endIcon={step < TOTAL_STEPS ? <ArrowForwardIcon /> : null}
                    sx={{
                      bgcolor: '#1E3A5F', textTransform: 'none', fontWeight: 700, px: 4,
                      '&:hover': { bgcolor: '#1A3152' },
                      '&.Mui-disabled': { bgcolor: '#EEF0F3', color: '#5F6B7A' },
                    }}
                  >
                    {step < TOTAL_STEPS ? t('calc.next') : t('calc.seeResults')}
                  </Button>
                </Box>
              </Box>
            </Grid>

            {/* Sticky summary — desktop only */}
            {!isMobile && (
              <Grid item md={5}>
                <StickyResumen
                  data={{ province, yearsInCanada, objective, income, employerMatch }}
                  currentStep={step}
                  onGoToStep={(s) => setStep(s)}
                />
              </Grid>
            )}
          </Grid>
        )}

        {/* ── RESULTS ── */}
        {showResults && result && (
          <Box id="calc-results" sx={{ mt: 2 }}>
            {/* Block A: Priority recommendation */}
            <Box sx={{ mb: 5 }}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#1A1A1A', mb: 1 }}>
                {t('calc.results.title')}
              </Typography>

              {/* Why for you bullets */}
              <Box sx={{ mb: 3, p: 2.5, bgcolor: '#F7F9FC', borderRadius: 3 }}>
                <Typography sx={{ fontSize: 14, color: '#1A1A1A', mb: 0.5, lineHeight: 1.7 }}>
                  {t('calc.results.marginalRate', {
                    rate: `${(result.marginalRate * 100).toFixed(1)}%`,
                    saving: Math.round(result.marginalRate * 1000).toLocaleString(),
                  })}
                </Typography>
                {objective === 'home' && (
                  <Typography sx={{ fontSize: 14, color: '#1A1A1A', lineHeight: 1.7 }}>
                    {t('calc.results.homeGoal')}
                  </Typography>
                )}
                {objective === 'taxes' && (
                  <Typography sx={{ fontSize: 14, color: '#1A1A1A', lineHeight: 1.7 }}>
                    {t('calc.results.taxGoal')}
                  </Typography>
                )}
                {objective === 'flexibility' && (
                  <Typography sx={{ fontSize: 14, color: '#1A1A1A', lineHeight: 1.7 }}>
                    {t('calc.results.flexGoal')}
                  </Typography>
                )}
                {objective === 'retirement' && (
                  <Typography sx={{ fontSize: 14, color: '#1A1A1A', lineHeight: 1.7 }}>
                    {t('calc.results.retireGoal')}
                  </Typography>
                )}
                {employerMatch === 'yes' && (
                  <Typography sx={{ fontSize: 14, color: '#1A1A1A', lineHeight: 1.7 }}>
                    {t('calc.results.hasMatch')}
                  </Typography>
                )}
                {employerMatch !== 'yes' && (
                  <Typography sx={{ fontSize: 14, color: '#1A1A1A', lineHeight: 1.7 }}>
                    {t('calc.results.noMatch')}
                  </Typography>
                )}
              </Box>

              <PriorityCards priority={result.priority} marginalRate={result.marginalRate} />
            </Box>

            {/* Block B: Comparison table */}
            <Box sx={{ mb: 5 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1A1A1A', mb: 2, fontSize: '1.1rem' }}>
                {t('calc.comparison.title')}
              </Typography>
              <ComparisonTable
                tfsaRoom={result.tfsaRoom}
                rrspRoom={result.rrspRoom}
                rrspSaving={result.rrspRoom * result.marginalRate}
                fhsaSaving={TAX_DATA.fhsaAnnual * result.marginalRate}
                recommended={result.priority[0]?.account}
                residencyYear={yearsInCanada === 'before2020' ? 2019 : yearsInCanada === 'unknown' ? null : Number(yearsInCanada) || null}
              />
            </Box>

            {/* Block C: Projection */}
            <Box sx={{ mb: 5, p: { xs: 2, md: 4 }, bgcolor: '#FAFBFD', borderRadius: 4 }}>
              <ProjectionChart
                contribution={Number(contribution) || 500}
                marginalRate={result.marginalRate}
                frequency={frequency}
              />
            </Box>

            {/* Block D: Next steps */}
            <Box sx={{ mb: 5 }}>
              <NextSteps objective={objective} employerMatch={employerMatch} />
            </Box>

            {/* Wealthsimple CTA */}
            <Box sx={{ bgcolor: '#F7F9FC', borderRadius: '12px', p: { xs: 2.5, md: 3 }, mb: 4, border: '1px solid #EEF0F3', textAlign: 'center' }}>
              <Typography sx={{ fontWeight: 600, fontSize: '15px', color: '#1A1A1A', mb: 0.5 }}>
                {lng === 'fr' ? 'Ouvrez votre REER en quelques minutes' : lng === 'en' ? 'Open your RRSP in minutes' : 'Abre tu RRSP en minutos'}
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#5F6B7A', lineHeight: 1.7, mb: 2 }}>
                {lng === 'fr'
                  ? 'Wealthsimple offre des REER et CÉLI sans frais cachés — idéal pour les nouveaux arrivants.'
                  : lng === 'en'
                  ? 'Wealthsimple offers RRSPs and TFSAs with no hidden fees — ideal for newcomers.'
                  : 'Wealthsimple ofrece RRSP y TFSA sin comisiones ocultas — ideal para recién llegados.'}
              </Typography>
              <ReferralCTA bank="wealthsimple">
                {lng === 'fr' ? 'Ouvrir un compte Wealthsimple' : lng === 'en' ? 'Open a Wealthsimple account' : 'Abrir cuenta Wealthsimple'}
              </ReferralCTA>
            </Box>

            {/* Block E: Share / Email */}
            <Box sx={{ mb: 5 }}>
              <ShareBar data={{ province, yearsInCanada, objective, income, employerMatch, contribution }} />
            </Box>

            {/* Edit / Restart buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 4 }}>
              <Button
                variant="outlined"
                onClick={handleEditFromResults}
                sx={{ textTransform: 'none', borderColor: '#EEF0F3', color: '#1E3A5F' }}
              >
                {t('calc.summary.editAssumptions')}
              </Button>
              <Button
                variant="outlined"
                onClick={handleRestart}
                sx={{ textTransform: 'none', borderColor: '#EEF0F3', color: '#D32F2F' }}
              >
                {t('calc.restart')}
              </Button>
            </Box>
          </Box>
        )}

        <Divider sx={{ my: 5, borderColor: '#EEF0F3' }} />

        {/* ── SEO CONTENT ── */}

        {/* Methodology accordion */}
        <Box sx={{ mb: 5 }}>
          <Accordion
            disableGutters elevation={0}
            sx={{ border: '1px solid #EEF0F3', borderRadius: '14px !important', '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 700, color: '#1A1A1A' }}>
                {t('calc.methodology.title')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography sx={{ fontSize: 14, color: '#5F6B7A', mb: 2, lineHeight: 1.7 }}>
                {t('calc.methodology.intro')}
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>
                {t('calc.methodology.sources')}
              </Typography>
              <Box component="ul" sx={{ pl: 2.5, mb: 2, '& li': { fontSize: 13, color: '#5F6B7A', mb: 0.5 } }}>
                <li>{t('calc.methodology.source1')}</li>
                <li>{t('calc.methodology.source2')}</li>
                <li>{t('calc.methodology.source3')}</li>
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>
                {t('calc.methodology.whatWeDoNot')}
              </Typography>
              <Typography sx={{ fontSize: 13, color: '#5F6B7A', lineHeight: 1.7 }}>
                {t('calc.methodology.disclaimer')}
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* FAQs */}
        <FAQSection faqs={faqs} />

        {/* Cross-links */}
        <Box sx={{ mt: 5, mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1A1A1A', mb: 2 }}>
            {t('calc.related.title')}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography
              component={RouterLink}
              to={getPath('blogTFSA')}
              sx={{ fontSize: 14, color: '#1E3A5F', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              {t('calc.related.guide')}
            </Typography>
            <Typography
              component={RouterLink}
              to={getPath('blogGuiaQuebec')}
              sx={{ fontSize: 14, color: '#1E3A5F', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              {t('calc.related.taxes')}
            </Typography>
            <Typography
              component={RouterLink}
              to={getPath('blogHipoteca')}
              sx={{ fontSize: 14, color: '#1E3A5F', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              {t('calc.related.mortgage')}
            </Typography>
            <Typography
              component={RouterLink}
              to={getPath('mortgageCalc')}
              sx={{ fontSize: 14, color: '#1E3A5F', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              {t('calc.related.mortgageCalc')}
            </Typography>
          </Box>
        </Box>

        <NewsletterSection />
      </Container>
    </>
  );
};

export default CalculadoraRRSPPage;
